import React, { useEffect, useState } from 'react';
import { AlertTriangle, Eye, EyeOff, Loader2, Plug, Unplug } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import { CopyButton, dtfmt } from './supportCommon';
import { EFOOD_INTEGRATOR } from '../platformDefaults';

/**
 * Κάρτα καταστήματος → «Πλατφόρμες» (25/10/2026): efood / Wolt / BOX. Η λήψη παραγγελιών εξαρτάται μόνο από τη σύνδεση
 * (id καταστήματος στην πλατφόρμα + webhook secret, και API key για τη Wolt) — όχι από τη σημαία. Το support βλέπει και
 * τα μυστικά (απόφαση χρήστη): κρυμμένα, με «Εμφάνιση» και αντιγραφή. Κενό πεδίο μυστικού στην αποθήκευση = μένει το
 * υπάρχον. «Αποσύνδεση» = σταματά η λήψη.
 */

interface Credentials {
  restaurant_id: string | null;
  api_username: string | null;
  api_password: string | null;
  api_key: string | null;
  webhook_secret: string | null;
  est_delivery_time: number | null;
  is_self_delivery: boolean;
  auto_accept_orders: boolean;
  preset_times: number[] | null;
  auto_print_tickets: boolean;
}
export interface PlatformState {
  platform: 'efood' | 'wolt' | 'box';
  flag: boolean;
  connected: boolean;
  credentials: Credentials | null;
  shared_webhook_secret: boolean | null;
  last_order_at: string | null;
  orders_30d: number;
  pending_accept: number;
  duplicate_stores: number[];
}

const NAMES = { efood: 'efood', wolt: 'Wolt', box: 'BOX' } as const;
// Τι λέγεται «id καταστήματος» σε κάθε πλατφόρμα, και ποια στοιχεία σύνδεσης χρειάζεται.
const META = {
  efood: { rid: 'Vendor ID (vendor_external_id)', user: 'Χρήστης integrator', pass: true, key: false },
  wolt: { rid: 'Venue ID', user: null, pass: false, key: true },
  box: { rid: 'Shop ID', user: 'Company', pass: false, key: true },
} as const;

const INPUT = 'w-full bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg px-3 py-2 text-sm';
const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;

/** Προειδοποιήσεις μιας πλατφόρμας — ό,τι κόβει ή μπερδεύει παραγγελίες. */
export const platformWarnings = (p: PlatformState): string[] => {
  const w: string[] = [];
  const c = p.credentials;
  if (p.duplicate_stores.length) w.push(`Το ίδιο id έχει και το κατάστημα #${p.duplicate_stores.join(', #')} — οι παραγγελίες πάνε σε όποιο βρεθεί πρώτο.`);
  // Χωρίς id καταστήματος η γραμμή δεν ταιριάζει με καμία παραγγελία — τότε ισχύει μόνο το «λείπει το id».
  if (p.connected && !p.flag && c?.restaurant_id) w.push('Παίρνει παραγγελίες, αλλά η πλατφόρμα φαίνεται κλειστή στο κατάστημα.');
  if (!p.connected && p.flag) w.push('Φαίνεται ενεργή, αλλά δεν είναι συνδεδεμένη — δεν έρχεται καμία παραγγελία.');
  if (c && !c.restaurant_id) w.push('Λείπει το id του καταστήματος — δεν έρχεται καμία παραγγελία.');
  if (c && !c.webhook_secret) w.push('Λείπει το webhook secret — η πλατφόρμα θα απορρίπτεται (401).');
  if (c && p.platform === 'wolt' && !c.api_key) w.push('Λείπει το API key — η Wolt δεν φέρνει καμία παραγγελία.');
  if (c && p.platform === 'efood' && (!c.api_username || !c.api_password)) w.push('Λείπουν τα στοιχεία integrator — οι παραγγελίες έρχονται αλλά δεν γίνονται αποδεκτές.');
  if (p.pending_accept) w.push(`${p.pending_accept} παραγγελίες περιμένουν αποδοχή.`);
  return w;
};

const Secret: React.FC<{ id: string; label: string; value: string; saved: string | null; onChange: (v: string) => void }> = ({ id, label, value, saved, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-t2 mb-1">{label}</label>
      <div className="flex gap-1.5">
        <input id={id} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="new-password"
          placeholder={saved ? '•••••• αποθηκευμένο' : ''} title={saved ? 'Κενό = μένει το αποθηκευμένο' : undefined}
          className={`${INPUT} font-mono`} spellCheck={false} />
        {saved && (
          <button type="button" onClick={() => setShow(s => !s)} className="p-2 text-t3 hover:text-t1 border border-line rounded-lg"
            title={show ? 'Απόκρυψη' : 'Εμφάνιση αποθηκευμένου'} aria-label={show ? `Απόκρυψη ${label}` : `Εμφάνιση ${label}`}>
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        {saved && <CopyButton value={saved} title={`Αντιγραφή ${label}`} />}
      </div>
      {saved && show && <p className="mt-1 text-xs font-mono text-t2 break-all" data-testid={`${id}-saved`}>{saved}</p>}
    </div>
  );
};

const PlatformCard: React.FC<{ storeId: number; p: PlatformState; onSaved: (list: PlatformState[], msg: string) => void }> = ({ storeId, p, onSaved }) => {
  const c = p.credentials;
  const meta = META[p.platform];
  const blank = () => ({
    restaurant_id: c?.restaurant_id || '',
    // efood: ο λογαριασμός integrator της αλυσίδας, όπως στην οθόνη του καταστήματος.
    api_username: c ? '' : (p.platform === 'efood' ? EFOOD_INTEGRATOR.username : ''),
    api_password: c ? '' : (p.platform === 'efood' ? EFOOD_INTEGRATOR.password : ''),
    api_key: '',
    webhook_secret: '',
    est_delivery_time: c?.est_delivery_time != null ? String(c.est_delivery_time) : '',
    is_self_delivery: !!c?.is_self_delivery,
    auto_accept_orders: !!c?.auto_accept_orders,
    auto_print_tickets: c ? c.auto_print_tickets : true,
    preset_times: (c?.preset_times || [15, 20, 30, 45]).join(', '),
  });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setF(blank()); setErr(null); }, [p]);
  const set = (k: keyof ReturnType<typeof blank>, v: any) => setF(prev => ({ ...prev, [k]: v }));
  const warnings = platformWarnings(p);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        restaurant_id: f.restaurant_id,
        est_delivery_time: f.est_delivery_time === '' ? null : Number(f.est_delivery_time),
        is_self_delivery: f.is_self_delivery,
        auto_accept_orders: f.auto_accept_orders,
        auto_print_tickets: f.auto_print_tickets,
        preset_times: f.preset_times.split(/[,\s]+/).map(Number).filter(n => Number.isInteger(n) && n > 0),
      };
      for (const k of ['api_username', 'api_password', 'api_key', 'webhook_secret'] as const) if (f[k]) body[k] = f[k];
      const d: any = await supportAPI.savePlatform(storeId, p.platform, body);
      onSaved(d?.platforms || [], `${NAMES[p.platform]}: ${c ? 'αποθηκεύτηκε' : 'συνδέθηκε'}.`);
    } catch (e) { setErr(errText(e, 'Η αποθήκευση απέτυχε.')); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setConfirmOff(false); setBusy(true); setErr(null);
    try {
      const d: any = await supportAPI.disconnectPlatform(storeId, p.platform);
      onSaved(d?.platforms || [], `${NAMES[p.platform]}: αποσυνδέθηκε — δεν έρχονται πια παραγγελίες.`);
    } catch (e) { setErr(errText(e, 'Η αποσύνδεση απέτυχε.')); } finally { setBusy(false); }
  };

  const id = (k: string) => `support-pl-${p.platform}-${k}`;
  return (
    <section className="border border-line-strong rounded-xl p-4 space-y-3 bg-surface-2 dark:bg-surface-2/40" data-testid={`support-platform-${p.platform}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-t1 flex items-center gap-2">
          {NAMES[p.platform]}
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.connected ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-surface-3 text-t3'}`}>
            {p.connected ? 'Συνδεδεμένο' : 'Όχι συνδεδεμένο'}
          </span>
        </h4>
        <p className="text-xs text-t3">
          {p.last_order_at ? `Τελευταία παραγγελία ${dtfmt(p.last_order_at)} · ${p.orders_30d} σε 30 ημέρες` : 'Καμία παραγγελία ακόμη'}
        </p>
      </div>
      {warnings.length > 0 && (
        <ul className="space-y-1" data-testid={`support-platform-${p.platform}-warnings`}>
          {warnings.map(w => (
            <li key={w} className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{w}</li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={id('rid')} className="block text-xs font-medium text-t2 mb-1">{meta.rid}</label>
          <input id={id('rid')} value={f.restaurant_id} onChange={(e) => set('restaurant_id', e.target.value)} className={`${INPUT} font-mono`} spellCheck={false} />
        </div>
        <Secret id={id('secret')} label="Webhook secret" value={f.webhook_secret} saved={c?.webhook_secret || null} onChange={(v) => set('webhook_secret', v)} />
        {meta.user && <Secret id={id('user')} label={meta.user} value={f.api_username} saved={c?.api_username || null} onChange={(v) => set('api_username', v)} />}
        {meta.pass && <Secret id={id('pass')} label="Κωδικός integrator" value={f.api_password} saved={c?.api_password || null} onChange={(v) => set('api_password', v)} />}
        {meta.key && <Secret id={id('key')} label="API key" value={f.api_key} saved={c?.api_key || null} onChange={(v) => set('api_key', v)} />}
        <div>
          <label htmlFor={id('eta')} className="block text-xs font-medium text-t2 mb-1">Προτεινόμενος χρόνος (λεπτά)</label>
          <input id={id('eta')} inputMode="numeric" value={f.est_delivery_time} onChange={(e) => set('est_delivery_time', e.target.value.replace(/\D/g, ''))} className={INPUT} />
        </div>
        <div>
          <label htmlFor={id('times')} className="block text-xs font-medium text-t2 mb-1">Γρήγοροι χρόνοι αποδοχής</label>
          <input id={id('times')} value={f.preset_times} onChange={(e) => set('preset_times', e.target.value)} className={INPUT} placeholder="15, 20, 30, 45" />
        </div>
      </div>
      {c?.webhook_secret && p.shared_webhook_secret === false && (
        <p className="text-xs text-t3">Το webhook secret δεν είναι το κοινό — ταιριάζει μόνο αν το ίδιο έχει μπει και στο portal της πλατφόρμας.</p>
      )}
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-t2">
        {([['auto_accept_orders', 'Αυτόματη αποδοχή'], ['auto_print_tickets', 'Δελτία κουζίνας'], ['is_self_delivery', 'Δική μας διανομή']] as const).map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-2">
            <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={id(k)} />{l}
          </label>
        ))}
      </div>
      {err && <p role="alert" className="text-sm text-red-700 dark:text-red-300" data-testid={id('error')}>{err}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {p.connected ? (
          confirmOff ? (
            <span className="inline-flex items-center gap-2 text-xs">
              <span className="text-red-700 dark:text-red-300">Σταματά η λήψη παραγγελιών {NAMES[p.platform]};</span>
              <button type="button" onClick={disconnect} className="px-2 py-1 rounded-md bg-red-600 text-white" data-testid={id('disconnect-confirm')}>Αποσύνδεση</button>
              <button type="button" onClick={() => setConfirmOff(false)} className="px-2 py-1 rounded-md border border-line text-t2">Άκυρο</button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmOff(true)} disabled={busy} data-testid={id('disconnect')}
              className="inline-flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300 hover:underline disabled:opacity-50">
              <Unplug className="h-3.5 w-3.5" />Αποσύνδεση
            </button>
          )
        ) : <span />}
        <button type="button" onClick={save} disabled={busy || (!c && !f.restaurant_id.trim())} data-testid={id('save')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}{c ? 'Αποθήκευση' : 'Σύνδεση'}
        </button>
      </div>
    </section>
  );
};

const SupportPlatforms: React.FC<{ storeId: number; onChanged?: (list: PlatformState[]) => void }> = ({ storeId, onChanged }) => {
  const [list, setList] = useState<PlatformState[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try { const d: any = await supportAPI.getPlatforms(storeId); setList(d?.platforms || []); onChanged?.(d?.platforms || []); }
      catch { setFailed(true); setList([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);
  if (list === null) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>;
  if (failed) return <p className="text-sm text-red-700 dark:text-red-300">Οι πλατφόρμες δεν φορτώθηκαν.</p>;
  return (
    <div className="space-y-3" data-testid="support-platforms">
      {msg && <p role="status" className="text-sm rounded-lg px-3 py-2 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300" data-testid="support-platforms-msg">{msg}</p>}
      {list.map(p => (
        <PlatformCard key={p.platform} storeId={storeId} p={p} onSaved={(next, m) => { setList(next); setMsg(m); onChanged?.(next); }} />
      ))}
    </div>
  );
};

export default SupportPlatforms;
